import { useRef, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatBookingDateShort, localeTagFromLang } from '../../utils/bookingLocaleDisplay';
import { stripEmojiUnicode } from '../../utils/stripEmoji';
import './BookingComponents.css';

interface Slot {
  id: string;
  startTime: string;
  endTime?: string;
  available: boolean;
  available_capacity?: number;
}

interface TimeSlot {
  timeString: string; // "16:30"
  slots: Slot[];
  totalCapacity: number;
  representativeSlotId: string;
}

interface SlotSelectionProps {
  date?: string;
  formattedDate?: string;
  timeSlots?: TimeSlot[];
  slots?: Slot[];
  onSelect: (slotId: string, timeString?: string) => void;
  language?: string;
  i18n?: { title?: string; subtitle?: string };
  requestedPartySize?: number;
}

export function SlotSelection({
  date,
  timeSlots,
  slots,
  onSelect,
  language = 'es',
  i18n,
  requestedPartySize,
}: SlotSelectionProps) {
  if (date && timeSlots) {
    return (
      <TimeSlotSelection
        date={date}
        timeSlots={timeSlots}
        onSelect={onSelect}
        language={language}
        i18n={i18n}
        requestedPartySize={requestedPartySize}
      />
    );
  }
  return <LegacySlotSelection slots={slots || []} onSelect={onSelect} language={language} i18n={i18n} />;
}

function CapacityBadge({
  totalCapacity,
  requestedPartySize,
  tl,
}: {
  totalCapacity: number;
  requestedPartySize: number;
  tl: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const cap = totalCapacity;
  const pax = requestedPartySize;
  if (cap <= 0) return null;
  if (cap >= pax * 3 && cap >= 10) return null;
  const isLow = cap <= pax + 2;
  return (
    <span className={`slot-pill-badge${isLow ? ' slot-pill-badge--low' : ''}`}>
      {isLow
        ? tl('booking.slots_few_left', { count: cap, defaultValue: `Quedan ${cap}` })
        : tl('booking.slots_available_short', { count: cap })}
    </span>
  );
}

function TimeSlotSelection({
  date,
  timeSlots,
  onSelect,
  language = 'es',
  requestedPartySize,
}: {
  date: string;
  timeSlots: TimeSlot[];
  onSelect: (slotId: string, timeString?: string) => void;
  language?: string;
  i18n?: { title?: string; subtitle?: string };
  requestedPartySize?: number;
}) {
  const { t } = useTranslation();
  const tl = (key: string, opts?: Record<string, unknown>) =>
    t(key, { lng: language, ...(opts || {}) });
  const localeTag = localeTagFromLang(language);
  const displayDate = formatBookingDateShort(date, localeTag);
  const pax = requestedPartySize ?? 1;

  return (
    <div className="booking-step booking-step--flow-left">
      <h2 className="booking-step-heading">{stripEmojiUnicode(tl('booking.select_time').replace(/:$/, ''))}</h2>
      <p className="slot-date-label">{displayDate}</p>
      <div className="slot-pill-list">
        {timeSlots.map((timeSlot) => (
          <button
            key={timeSlot.timeString}
            type="button"
            className="slot-pill"
            onClick={() => onSelect(timeSlot.representativeSlotId, timeSlot.timeString)}
          >
            <span className="slot-pill-time">{timeSlot.timeString}</span>
            <CapacityBadge totalCapacity={timeSlot.totalCapacity} requestedPartySize={pax} tl={tl} />
          </button>
        ))}
      </div>
    </div>
  );
}

function parseIsoDateLocal(d: string): Date {
  const [y, m, day] = d.slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, day || 1);
}

function weekdayNarrow(locale: string, d: Date): string {
  try {
    return new Intl.DateTimeFormat(locale, { weekday: 'narrow' }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en', { weekday: 'narrow' }).format(d);
  }
}

function LegacySlotSelection({
  slots,
  onSelect,
  language = 'es',
}: {
  slots: Slot[];
  onSelect: (slotId: string) => void;
  language?: string;
  i18n?: { title?: string; subtitle?: string };
}) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const firstAvailableRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const tl = (key: string, opts?: Record<string, unknown>) =>
    t(key, { lng: language, ...(opts || {}) });
  const localeTag = localeTagFromLang(language);

  const slotsByDate = useMemo(() => {
    return slots.reduce(
      (acc, slot) => {
        if (!slot.available) return acc;
        const start = new Date(slot.startTime);
        const date = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
        if (!acc[date]) acc[date] = [];
        acc[date].push(slot);
        return acc;
      },
      {} as Record<string, Slot[]>,
    );
  }, [slots]);

  const dates = Object.keys(slotsByDate).sort();
  const firstAvailableDate = dates[0];

  useEffect(() => {
    firstAvailableRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  }, [dates]);

  const formatTime = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleTimeString(localeTag, { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const getTimeSlotsForDate = (date: string) => {
    const dateSlots = slotsByDate[date] || [];
    const timeGroups = dateSlots.reduce(
      (acc, slot) => {
        const timeString = formatTime(slot.startTime);
        if (!acc[timeString]) acc[timeString] = [];
        acc[timeString].push(slot);
        return acc;
      },
      {} as Record<string, Slot[]>,
    );
    return Object.entries(timeGroups)
      .map(([timeString, slotGroup]) => ({
        timeString,
        totalCapacity: slotGroup.reduce((sum, s) => sum + (s.available_capacity || 1), 0),
        representativeSlot: slotGroup[0],
      }))
      .sort((a, b) => {
        const [ah, am] = a.timeString.split(':').map(Number);
        const [bh, bm] = b.timeString.split(':').map(Number);
        return ah !== bh ? ah - bh : am - bm;
      });
  };

  const timeSlots = selectedDate ? getTimeSlotsForDate(selectedDate) : [];

  return (
    <div className="booking-step booking-step--flow-left">
      <h2 className="booking-step-heading">{stripEmojiUnicode(tl('booking.select_date_and_time'))}</h2>
      {dates.length === 0 ? (
        <p>{tl('booking.no_slots')}</p>
      ) : (
        <>
          <div className="date-strip-wrap">
            <div className="date-strip" role="group" aria-label={tl('booking.select_date')}>
              {dates.map((iso) => {
                const d = parseIsoDateLocal(iso);
                const weekday = weekdayNarrow(localeTag, d);
                const dayNum = d.getDate();
                const monthStr = d.toLocaleString(localeTag, { month: 'short' }).replace('.', '');
                const isSelected = iso === selectedDate;
                const isFirstAvailable = iso === firstAvailableDate;
                return (
                  <button
                    key={iso}
                    ref={isFirstAvailable ? firstAvailableRef : undefined}
                    type="button"
                    aria-selected={isSelected}
                    className={['date-strip-item', isSelected ? 'date-strip-item--selected' : '']
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setSelectedDate(iso)}
                  >
                    <span className="date-strip-weekday">{weekday}</span>
                    <span className="date-strip-day">{dayNum}</span>
                    <span className="date-strip-month">{monthStr}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {selectedDate && timeSlots.length > 0 && (
            <div className="slot-pill-list" style={{ marginTop: '1.25rem' }}>
              {timeSlots.map((ts) => (
                <button
                  key={ts.timeString}
                  type="button"
                  className="slot-pill"
                  onClick={() => onSelect(ts.representativeSlot.id)}
                >
                  <span className="slot-pill-time">{ts.timeString}</span>
                  {ts.totalCapacity > 1 && (
                    <span className="slot-pill-badge">
                      {tl('booking.slots_available_short', { count: ts.totalCapacity })}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {selectedDate && timeSlots.length === 0 && (
            <p className="no-slots-message">{tl('booking.no_slots_for_date')}</p>
          )}
        </>
      )}
    </div>
  );
}
